import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DialogControl } from 'spark-core-dx/components';
import './mcpOAuthConnect.scss';

const base64Url = (bytes) => btoa(String.fromCharCode(...bytes))
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/g, '');

const randomBase64Url = (length = 32) => {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return base64Url(bytes);
};

const sha256Base64Url = async (value) => {
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return base64Url(new Uint8Array(digest));
};

const buildFormBody = (values) => {
  const body = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim()) {
      body.set(key, value);
    }
  });
  return body;
};

const readValue = (source, ...keys) => {
  if (!source || typeof source !== 'object') return '';
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
};

const formatStatusValue = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  return value === undefined || value === null || value === '' ? 'Not reported' : String(value);
};

const parseResponseBody = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { body: text };
  }
};

const McpOAuthConnect = () => {
  const { clientShortName, deploymentName } = useParams();
  const [searchParams] = useSearchParams();
  const flow = searchParams.get('flow') ?? '';
  const callbackCode = searchParams.get('code') ?? '';
  const callbackState = searchParams.get('state') ?? '';
  const callbackError = searchParams.get('error_description') || searchParams.get('error') || '';
  const startPath = `/mcp/${encodeURIComponent(clientShortName ?? '')}/oauth/entra/start?flow=${encodeURIComponent(flow)}`;
  const configuredApiUri = window?.configData?.API_URIS?.CONVOKE ?? '';
  const apiIndex = configuredApiUri.toLowerCase().lastIndexOf('/api/');
  const apiBaseUri = (apiIndex >= 0 ? configuredApiUri.slice(0, apiIndex) : configuredApiUri).replace(/\/+$/, '');
  const mcpServerUrl = deploymentName
    ? `${apiBaseUri || window.location.origin}/mcp/${encodeURIComponent(clientShortName ?? '')}/V1/${encodeURIComponent(deploymentName)}`
    : '';
  const mcpStatusUrl = mcpServerUrl ? `${mcpServerUrl}/status` : '';
  const hasOAuthFlow = Boolean(flow);
  const hasCallback = Boolean(callbackCode && callbackState);
  const [testStatus, setTestStatus] = useState(hasCallback ? 'Completing browser connection test...' : '');
  const [testError, setTestError] = useState(callbackError);
  const [isStartingTest, setIsStartingTest] = useState(false);
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    loading: false,
    status: '',
    message: '',
    facts: [],
    error: ''
  });
  const handledCallbackRef = useRef(false);

  const testStorageKey = useCallback((state) => `convoke.mcp.oauth.test.${state}`, []);

  const startBrowserOAuthTest = useCallback(async () => {
    if (!clientShortName || !deploymentName || !mcpServerUrl) return;

    setIsStartingTest(true);
    setTestStatus('Preparing browser connection test...');
    setTestError('');

    try {
      const redirectUri = `${window.location.origin}/mcp/${encodeURIComponent(clientShortName)}/V1/${encodeURIComponent(deploymentName)}/test-connect`;
      const codeVerifier = randomBase64Url(48);
      const codeChallenge = await sha256Base64Url(codeVerifier);
      const state = randomBase64Url(24);
      const registerResponse = await fetch(`${apiBaseUri}/mcp/${encodeURIComponent(clientShortName)}/oauth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: `Convoke MCP connection - ${deploymentName}`,
          redirect_uris: [redirectUri],
          token_endpoint_auth_method: 'none',
          scope: 'openid offline_access'
        })
      });

      if (!registerResponse.ok) {
        const detail = await registerResponse.text();
        throw new Error(detail || `Client registration failed with ${registerResponse.status}.`);
      }

      const registration = await registerResponse.json();
      sessionStorage.setItem(testStorageKey(state), JSON.stringify({
        clientId: registration.client_id,
        codeVerifier,
        redirectUri,
        resource: mcpServerUrl
      }));

      const authorizeUrl = `${apiBaseUri}/mcp/${encodeURIComponent(clientShortName)}/oauth/authorize?${buildFormBody({
        response_type: 'code',
        client_id: registration.client_id,
        redirect_uri: redirectUri,
        resource: mcpServerUrl,
        scope: 'openid offline_access',
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
      }).toString()}`;

      window.location.assign(authorizeUrl);
    } catch (error) {
      setTestStatus('');
      setTestError(error?.message || 'Unable to start the MCP OAuth test.');
      setIsStartingTest(false);
    }
  }, [apiBaseUri, clientShortName, deploymentName, mcpServerUrl, testStorageKey]);

  const closeStatusDialog = useCallback(() => {
    setStatusDialog({
      open: false,
      loading: false,
      status: '',
      message: '',
      facts: [],
      error: ''
    });
  }, []);

  const checkMcpStatus = useCallback(async () => {
    if (!mcpStatusUrl) return;

    setStatusDialog({
      open: true,
      loading: true,
      status: '',
      message: 'Checking MCP server status...',
      facts: [],
      error: ''
    });

    try {
      const response = await fetch(mcpStatusUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });
      const payload = await parseResponseBody(response);
      const api = readValue(payload, 'api', 'Api') || {};
      const auth = readValue(payload, 'authentication', 'Authentication') || {};
      const routes = readValue(payload, 'routes', 'Routes') || [];
      const endpointOnline = readValue(payload, 'endpointOnline', 'EndpointOnline');
      const mcpEnabled = readValue(api, 'mcpEnabled', 'McpEnabled');
      const deploymentKey = readValue(api, 'deploymentKey', 'DeploymentKey') || deploymentName;

      if (!response.ok) {
        const detail = readValue(payload, 'detail', 'Detail', 'message', 'Message', 'body') || `Status check returned ${response.status}.`;
        throw new Error(detail);
      }

      setStatusDialog({
        open: true,
        loading: false,
        status: 'success',
        message: endpointOnline === true
          ? 'MCP server is online.'
          : 'MCP status route responded. The deployment is configured and the status check completed.',
        facts: [
          { label: 'HTTP status', value: `${response.status} ${response.statusText || ''}`.trim() },
          { label: 'Client', value: readValue(api, 'clientShortName', 'ClientShortName') || clientShortName },
          { label: 'Deployment', value: deploymentKey },
          { label: 'MCP enabled', value: mcpEnabled },
          { label: 'Endpoint online', value: endpointOnline },
          { label: 'Auth mode', value: readValue(auth, 'authenticationMode', 'AuthenticationMode') },
          { label: 'Routes', value: Array.isArray(routes) ? routes.length : 0 }
        ],
        error: ''
      });
    } catch (error) {
      setStatusDialog({
        open: true,
        loading: false,
        status: 'error',
        message: 'MCP status check failed.',
        facts: [
          { label: 'Status URL', value: mcpStatusUrl }
        ],
        error: error?.message || 'Unable to check MCP status.'
      });
    }
  }, [clientShortName, deploymentName, mcpStatusUrl]);

  useEffect(() => {
    if (!hasCallback || handledCallbackRef.current || !clientShortName) return;
    handledCallbackRef.current = true;

    const completeOAuthTest = async () => {
      try {
        const stored = sessionStorage.getItem(testStorageKey(callbackState));
        if (!stored) {
          throw new Error('Connection state was not found in this browser session. Start the connection again.');
        }

        const test = JSON.parse(stored);
        const tokenResponse = await fetch(`${apiBaseUri}/mcp/${encodeURIComponent(clientShortName)}/oauth/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: buildFormBody({
            grant_type: 'authorization_code',
            client_id: test.clientId,
            code: callbackCode,
            redirect_uri: test.redirectUri,
            code_verifier: test.codeVerifier,
            resource: test.resource
          })
        });

        if (!tokenResponse.ok) {
          const detail = await tokenResponse.text();
          throw new Error(detail || `Token exchange failed with ${tokenResponse.status}.`);
        }

        sessionStorage.removeItem(testStorageKey(callbackState));
        setTestError('');
        setTestStatus('Browser callback test completed. The WebAPI issued tokens for this test flow, but this page does not authenticate Codex or store MCP refresh tokens for another client.');
      } catch (error) {
        setTestStatus('');
        setTestError(error?.message || 'Unable to complete the MCP OAuth test.');
      }
    };

    completeOAuthTest();
  }, [apiBaseUri, callbackCode, callbackState, clientShortName, hasCallback, testStorageKey]);

  return (
    <main className="mcp-oauth-connect">
      <section className="mcp-oauth-connect__panel">
        <div className="mcp-oauth-connect__brand">CONVOKE</div>
        <h1>{hasOAuthFlow ? "Log In To Continue" : "MCP Browser Connection Test"}</h1>
        <p>
          {hasOAuthFlow
            ? `Convoke signs you in with Entra and returns a short-lived authorization code to the requesting MCP client.`
            : `Use this page to verify the browser login and OAuth callback for ${deploymentName || "this deployment"}. It does not prove that Codex or another MCP client stored a refresh token.`}
        </p>
        {mcpServerUrl ? (
          <div className="mcp-oauth-connect__server">
            <span>MCP server URL</span>
            <code>{mcpServerUrl}</code>
          </div>
        ) : null}
        {testStatus ? <div className="mcp-oauth-connect__notice">{testStatus}</div> : null}
        {testError ? <div className="mcp-oauth-connect__notice mcp-oauth-connect__notice--error">{testError}</div> : null}
        {hasOAuthFlow ? (
          <>
            <ol className="mcp-oauth-connect__steps">
              <li>Confirm this MCP connection request.</li>
              <li>Sign in with Entra using an account allowed for this deployment.</li>
              <li>Return with a short-lived authorization code for the requesting MCP client.</li>
            </ol>
            <a className="mcp-oauth-connect__action" href={startPath}>
              Log in to connect
            </a>
          </>
        ) : (
          <>
            <ol className="mcp-oauth-connect__steps">
              <li>Start a browser-only MCP OAuth test request.</li>
              <li>Sign in with Entra and authorize this deployment.</li>
              <li>Return here to confirm the WebAPI callback and token issue path works.</li>
            </ol>
            {mcpStatusUrl ? (
              <div className="mcp-oauth-connect__actions">
                <button
                  type="button"
                  className="mcp-oauth-connect__action"
                  onClick={startBrowserOAuthTest}
                  disabled={isStartingTest}
                >
                  {isStartingTest ? 'Starting test...' : 'Run browser test'}
                </button>
                <button
                  type="button"
                  className="mcp-oauth-connect__action mcp-oauth-connect__action--secondary"
                  onClick={checkMcpStatus}
                  disabled={statusDialog.loading}
                >
                  {statusDialog.loading ? 'Checking status...' : 'Check MCP status'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
      <DialogControl
        openDialog={statusDialog.open}
        title="MCP Status"
        okText="OK"
        onOk={closeStatusDialog}
        onCancel={closeStatusDialog}
        onX={closeStatusDialog}
        excludeCancel
        disableOk={statusDialog.loading}
      >
        <div className="mcp-oauth-connect__status-dialog">
          <div className={`mcp-oauth-connect__notice ${statusDialog.status === 'error' ? 'mcp-oauth-connect__notice--error' : ''}`} role={statusDialog.status === 'error' ? 'alert' : 'status'}>
            {statusDialog.message}
          </div>
          {statusDialog.error ? (
            <div className="mcp-oauth-connect__status-error">{statusDialog.error}</div>
          ) : null}
          {statusDialog.facts.length ? (
            <dl className="mcp-oauth-connect__status-facts">
              {statusDialog.facts.map((fact) => (
                <div className="mcp-oauth-connect__status-fact" key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd>{formatStatusValue(fact.value)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </DialogControl>
    </main>
  );
};

export default McpOAuthConnect;
